import { motion } from "framer-motion";

// Animated heart with embedded CPR/heartbeat ECG line — drawn in SVG
const HeartWithBeat = ({ className = "" }) => (
    <svg viewBox="0 0 120 110" className={className} aria-hidden>
        <motion.path
            d="M60 100 C 60 100, 8 70, 8 38 C 8 22, 22 12, 36 12 C 48 12, 56 20, 60 28 C 64 20, 72 12, 84 12 C 98 12, 112 22, 112 38 C 112 70, 60 100, 60 100 Z"
            fill="none"
            stroke="#E63946"
            strokeWidth="3"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: "easeInOut" }}
        />
        <motion.path
            d="M14 55 L42 55 L48 42 L56 70 L64 32 L72 60 L80 50 L106 50"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.8, delay: 0.6, ease: "easeOut" }}
        />
    </svg>
);

// Stylized "This is JC's Legacy" with hand-drawn heart + heartbeat
const LegacyMark = () => (
    <div className="relative flex flex-col items-center justify-center text-center">
        <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="font-serif italic text-editorial text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight"
            style={{ fontWeight: 300 }}
        >
            This is
        </motion.p>
        <motion.p
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
            className="font-serif italic text-coral text-5xl md:text-6xl lg:text-7xl leading-[0.9] mt-1"
            style={{ fontWeight: 400 }}
        >
            JC's Legacy
        </motion.p>

        {/* Hand-drawn heartbeat heart */}
        <svg viewBox="0 0 200 180" className="w-44 md:w-56 mt-6" aria-hidden>
            <motion.path
                d="M100 160 C 100 160, 20 110, 20 60 C 20 38, 38 22, 60 22 C 78 22, 92 34, 100 50 C 108 34, 122 22, 140 22 C 162 22, 180 38, 180 60 C 180 110, 100 160, 100 160 Z"
                fill="none"
                stroke="#E63946"
                strokeWidth="2.2"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2, ease: "easeInOut" }}
            />
            <motion.path
                d="M28 88 L70 88 L78 70 L88 110 L98 50 L110 100 L120 80 L172 80"
                fill="none"
                stroke="#E63946"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 0.6, ease: "easeOut" }}
            />
        </svg>
    </div>
);

export const MissionStatement = () => (
    <section className="relative bg-navy-900 text-editorial overflow-hidden" data-testid="mission-statement">
        {/* Background subtle layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800/80" />
        <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />

        {/* Animated coral glow */}
        <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-coral/10 blur-3xl pointer-events-none"
            animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0 items-center">
            {/* LEFT — Logo lockup */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="flex flex-col items-center md:items-start text-center md:text-left md:pr-10 md:border-r md:border-coral/40"
            >
                <HeartWithBeat className="w-28 h-24 md:w-32 md:h-28 mb-6" />
                <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-editorial">
                    JC LAW
                </h2>
                <div className="mt-5 flex items-center gap-3">
                    <div className="w-10 h-px bg-coral" />
                    <p className="text-[10px] md:text-xs uppercase tracking-[0.28em] text-coral font-semibold">
                        Prepared Hotels
                    </p>
                </div>
                <p className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.28em] text-editorial/70 font-semibold">
                    Protected Lives
                </p>
            </motion.div>

            {/* CENTER — Mission body */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="md:px-10 md:border-r md:border-coral/40 text-center md:text-left"
            >
                <p className="font-sans font-bold uppercase tracking-[0.22em] text-base md:text-lg text-coral mb-5">
                    Our Mission
                </p>
                <p className="text-base md:text-lg text-editorial/90 leading-relaxed mb-4">
                    JC Law is dedicated to ensuring hotels are equipped with{" "}
                    <span className="text-coral font-semibold">AEDs</span>, staffed with{" "}
                    <span className="text-coral font-semibold">CPR-trained employees</span>, and prepared to respond to medical emergencies.
                </p>
                <p className="font-serif italic text-lg md:text-xl text-gold leading-snug">
                    Because every second matters — and every life is worth saving.
                </p>
            </motion.div>

            {/* RIGHT — JC's Legacy */}
            <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="md:pl-10"
            >
                <LegacyMark />
            </motion.div>
        </div>

        {/* Bottom hairline */}
        <div className="relative h-px bg-gradient-to-r from-transparent via-coral/60 to-transparent" />
    </section>
);

export default MissionStatement;
